import { useCallback, useEffect, useMemo, useState } from "react";

// ── CONFIGURATION ─────────────────────────────────────────────────────────────
// The password itself remains server-side in Vercel's ADMIN_PASSWORD env var.
// After a successful login, the typed password is kept only in sessionStorage
// and attached to protected requests as X-Admin-Password.
const ADMIN_TOKEN_KEY = "drag-admin-pw";
const STORAGE_RESULTS_KEY = "drag-official-results-v1";
const STORAGE_LB_KEY = "drag-lb-v3";

// ── FINAL ALL STARS 11 RESULTS ────────────────────────────────────────────────
// These results are deliberately baked into this final-season admin file.
// The admin no longer needs to enter or arrange any result manually.
const FINAL_RESULTS = Object.freeze({
  semiFinalists: Object.freeze({
    orange: Object.freeze(["Dawn", "A'Keria C. Davenport"]),
    pink: Object.freeze(["Crystal Methyd", "Silky Nutmeg Ganache"]),
    purple: Object.freeze(["Kennedy Davenport", "Sam Star", "Jasmine Kennedie"]),
  }),
  tiedBrackets: Object.freeze({
    orange: false,
    pink: true,
    purple: false,
  }),
  winner: "Crystal Methyd",
  runnerUp: "Joey Jay",
  seasonComplete: true,

  // Metadata is harmless to older readers and gives updated player pages a
  // single source of truth for the twist-adjusted denominator and explanation.
  scoringPolicyVersion: "as11-final-twist-adjusted-v1",
  originalMaxScore: 79,
  perfectScore: 73,
  scoringExplanation:
    "Ru introduced ties and a wildcard route that the original bracket could not fully represent. Genuine unresolved ties create alternate valid answers for an existing slot, but never extra scoring slots or extra points. Wildcard advancement does not retroactively earn bracket points; overall Winner and Runner-Up predictions are still scored normally. Because the original 79-point maximum became impossible under the site's required prediction path, 73 is the highest score that was actually achievable and therefore counts as Perfect.",
});

const PERFECT_SCORE = FINAL_RESULTS.perfectScore;

const BRACKETS = {
  orange: {
    label: "Orange Bracket",
    color: "#ff6b35",
  },
  pink: {
    label: "Pink Bracket",
    color: "#ff2d9a",
  },
  purple: {
    label: "Purple Bracket",
    color: "#9b5de5",
  },
};

const QUEEN_EMOJI = {
  "A'Keria C. Davenport": "💛",
  Dawn: "🌅",
  "Crystal Methyd": "💎",
  "Silky Nutmeg Ganache": "🍪",
  "Kennedy Davenport": "👑",
  "Sam Star": "⭐",
  "Jasmine Kennedie": "🌸",
  "Joey Jay": "🦘",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  :root {
    --dark: #08030e;
    --surface: #120820;
    --card: #1a0d2e;
    --card-2: #21113a;
    --gold: #f5c842;
    --pink: #ff2d9a;
    --muted: #9a7db7;
    --text: #ede5ff;
    --green: #4ade80;
    --red: #f87171;
  }

  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; min-height: 100%; background: var(--dark); color: var(--text); }
  body { font-family: 'DM Sans', sans-serif; }
  button, input { font: inherit; }

  .dm-admin { min-height: 100vh; background:
    radial-gradient(circle at 10% 0%, rgba(255,45,154,.09), transparent 28rem),
    radial-gradient(circle at 90% 0%, rgba(245,200,66,.07), transparent 26rem),
    var(--dark);
  }

  .dm-login-wrap {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 1.5rem;
  }

  .dm-login-card, .dm-card {
    background: rgba(26,13,46,.96);
    border: 1px solid rgba(255,255,255,.08);
    border-radius: 8px;
    box-shadow: 0 24px 80px rgba(0,0,0,.32);
  }

  .dm-login-card { width: min(390px, 100%); padding: 2rem; }
  .dm-brand {
    font-family: 'Bebas Neue', sans-serif;
    letter-spacing: .14em;
    font-size: 1.15rem;
    background: linear-gradient(90deg, var(--gold), var(--pink));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .dm-title { margin: .35rem 0 .25rem; font-size: 1.45rem; font-weight: 700; }
  .dm-sub { color: var(--muted); font-size: .84rem; line-height: 1.6; }
  .dm-label { display: block; margin: 1.25rem 0 .4rem; color: var(--muted); font-size: .68rem; letter-spacing: .14em; text-transform: uppercase; }
  .dm-input {
    width: 100%; padding: .72rem .85rem; border-radius: 4px;
    color: var(--text); background: rgba(255,255,255,.05);
    border: 1px solid rgba(255,255,255,.11); outline: none;
  }
  .dm-input:focus { border-color: var(--pink); }
  .dm-error { margin-top: .65rem; color: var(--red); font-size: .8rem; }

  .dm-btn {
    border: 0; border-radius: 4px; padding: .66rem 1rem; cursor: pointer;
    font-weight: 700; font-size: .73rem; letter-spacing: .09em; text-transform: uppercase;
    transition: transform .15s ease, filter .15s ease, opacity .15s ease;
  }
  .dm-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.07); }
  .dm-btn:disabled { opacity: .42; cursor: not-allowed; }
  .dm-btn-gold { background: var(--gold); color: #120820; }
  .dm-btn-pink { background: var(--pink); color: white; }
  .dm-btn-ghost { background: transparent; color: var(--muted); border: 1px solid rgba(255,255,255,.13); }
  .dm-btn-block { width: 100%; margin-top: 1rem; }

  .dm-nav {
    position: sticky; top: 0; z-index: 20;
    display: flex; align-items: center; justify-content: space-between; gap: 1rem;
    min-height: 60px; padding: .7rem 1.25rem;
    background: rgba(8,3,14,.92); backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(245,200,66,.13);
  }
  .dm-nav-left, .dm-nav-tabs, .dm-actions { display: flex; align-items: center; gap: .55rem; }
  .dm-badge {
    padding: .2rem .55rem; border-radius: 3px; color: var(--pink);
    background: rgba(255,45,154,.13); font-size: .62rem; font-weight: 700;
    letter-spacing: .13em; text-transform: uppercase;
  }
  .dm-tab {
    border: 0; background: transparent; color: var(--muted); cursor: pointer;
    padding: .43rem .7rem; border-radius: 4px; font-size: .7rem;
    letter-spacing: .08em; text-transform: uppercase;
  }
  .dm-tab.active { color: var(--gold); background: rgba(245,200,66,.1); }

  .dm-page { width: min(980px, calc(100% - 2rem)); margin: 0 auto; padding: 2rem 0 5rem; }
  .dm-page-head { margin-bottom: 1.4rem; }
  .dm-page-title { margin: 0 0 .25rem; font-size: 1.55rem; }
  .dm-page-sub { margin: 0; color: var(--muted); font-size: .84rem; line-height: 1.65; }

  .dm-banner {
    display: flex; align-items: flex-start; gap: .8rem;
    margin-bottom: 1.2rem; padding: 1rem 1.1rem; border-radius: 7px;
    border: 1px solid rgba(74,222,128,.24); background: rgba(74,222,128,.07);
  }
  .dm-banner strong { color: var(--green); }
  .dm-banner-copy { color: rgba(237,229,255,.82); font-size: .82rem; line-height: 1.6; }

  .dm-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .9rem; }
  .dm-card { overflow: hidden; }
  .dm-card-head {
    display: flex; align-items: center; gap: .65rem; padding: .85rem 1rem;
    border-bottom: 1px solid rgba(255,255,255,.06);
  }
  .dm-dot { width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto; }
  .dm-card-title { font-size: .88rem; font-weight: 700; flex: 1; }
  .dm-status {
    padding: .17rem .45rem; border-radius: 3px; background: rgba(74,222,128,.1);
    color: var(--green); font-size: .59rem; letter-spacing: .1em; text-transform: uppercase; font-weight: 700;
  }
  .dm-card-body { padding: 1rem; }
  .dm-result-row {
    display: flex; align-items: center; gap: .65rem; min-height: 42px;
    padding: .5rem .6rem; border-radius: 4px; background: rgba(255,255,255,.035);
    border: 1px solid rgba(255,255,255,.055); margin-bottom: .45rem;
  }
  .dm-result-rank {
    width: 27px; color: var(--gold); font-family: 'Bebas Neue', sans-serif;
    font-size: 1rem; text-align: center;
  }
  .dm-result-name { font-size: .81rem; font-weight: 600; flex: 1; }
  .dm-result-note { color: var(--muted); font-size: .68rem; line-height: 1.5; margin-top: .65rem; }

  .dm-final-card { margin-top: .9rem; }
  .dm-final-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }
  .dm-final-box {
    padding: 1rem; border-radius: 6px; background: rgba(255,255,255,.035);
    border: 1px solid rgba(255,255,255,.06);
  }
  .dm-kicker { color: var(--muted); font-size: .64rem; letter-spacing: .14em; text-transform: uppercase; }
  .dm-final-name { margin-top: .35rem; font-size: 1rem; font-weight: 700; }

  .dm-policy {
    margin-top: 1rem; padding: 1rem; border-radius: 6px;
    background: rgba(245,200,66,.055); border: 1px solid rgba(245,200,66,.17);
  }
  .dm-policy-title { color: var(--gold); font-weight: 700; font-size: .85rem; margin-bottom: .4rem; }
  .dm-policy-copy { color: rgba(237,229,255,.78); font-size: .79rem; line-height: 1.65; }
  .dm-score-pill {
    display: inline-block; margin-top: .65rem; padding: .25rem .55rem; border-radius: 3px;
    background: rgba(245,200,66,.12); color: var(--gold); font-size: .7rem; font-weight: 700;
  }

  .dm-publish {
    margin-top: 1rem; padding: 1.15rem; border-radius: 7px;
    display: flex; align-items: center; justify-content: space-between; gap: 1rem;
    background: var(--card); border: 1px solid rgba(255,255,255,.07);
  }
  .dm-publish-title { font-weight: 700; font-size: .9rem; }
  .dm-publish-sub { margin-top: .2rem; color: var(--muted); font-size: .76rem; line-height: 1.5; }

  .dm-stats { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: .8rem; margin-bottom: 1rem; }
  .dm-stat { padding: 1rem; background: var(--card); border: 1px solid rgba(255,255,255,.06); border-radius: 6px; }
  .dm-stat-num { display: block; color: var(--gold); font-family: 'Bebas Neue', sans-serif; font-size: 2rem; line-height: 1; }
  .dm-stat-label { margin-top: .25rem; color: var(--muted); font-size: .64rem; letter-spacing: .11em; text-transform: uppercase; }

  .dm-table-wrap { overflow-x: auto; background: var(--card); border: 1px solid rgba(255,255,255,.06); border-radius: 6px; }
  .dm-table { width: 100%; border-collapse: collapse; font-size: .8rem; }
  .dm-table th { color: var(--muted); font-size: .62rem; letter-spacing: .12em; text-transform: uppercase; text-align: left; font-weight: 600; }
  .dm-table th, .dm-table td { padding: .7rem .8rem; border-bottom: 1px solid rgba(255,255,255,.045); }
  .dm-table tr:last-child td { border-bottom: 0; }
  .dm-rank { color: var(--gold); font-family: 'Bebas Neue', sans-serif; font-size: 1rem; }
  .dm-score { color: var(--gold); font-weight: 700; }
  .dm-perfect { color: var(--green); font-size: .66rem; font-weight: 700; margin-left: .35rem; }
  .dm-empty { padding: 3rem 1rem; text-align: center; color: var(--muted); }

  .dm-email-card { padding: 1.2rem; }
  .dm-log { margin-top: 1rem; max-height: 260px; overflow: auto; padding: .8rem 1rem; border-radius: 5px; background: rgba(0,0,0,.28); font: .74rem/1.75 monospace; color: var(--muted); }
  .dm-log-ok { color: var(--green); }
  .dm-log-err { color: var(--red); }
  .dm-log-info { color: var(--gold); }

  .dm-toast {
    position: fixed; z-index: 60; left: 50%; bottom: 1.5rem;
    transform: translate(-50%, 100px); transition: transform .3s ease;
    background: var(--surface); border: 1px solid var(--gold); border-radius: 4px;
    padding: .7rem 1rem; color: var(--text); font-size: .8rem;
  }
  .dm-toast.show { transform: translate(-50%, 0); }

  @media (max-width: 760px) {
    .dm-nav { align-items: flex-start; flex-wrap: wrap; }
    .dm-nav-tabs { order: 3; width: 100%; overflow-x: auto; }
    .dm-grid, .dm-stats { grid-template-columns: 1fr; }
    .dm-final-grid { grid-template-columns: 1fr; }
    .dm-publish { align-items: stretch; flex-direction: column; }
  }
`;

function safeParse(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
}

function resultsFingerprint(results) {
  if (!results) return "";
  return JSON.stringify({
    semiFinalists: results.semiFinalists || {},
    tiedBrackets: results.tiedBrackets || {},
    winner: results.winner || null,
    runnerUp: results.runnerUp || null,
    seasonComplete: Boolean(results.seasonComplete),
  });
}

function cloneFinalResults() {
  return JSON.parse(JSON.stringify(FINAL_RESULTS));
}

export default function AdminPanel() {
  const [authed, setAuthed] = useState(() => {
    try {
      return Boolean(sessionStorage.getItem(ADMIN_TOKEN_KEY));
    } catch (_) {
      return false;
    }
  });
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [savedResults, setSavedResults] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [activeTab, setActiveTab] = useState("results");
  const [toast, setToast] = useState("");
  const [toastVisible, setToastVisible] = useState(false);

  const showToast = useCallback((message) => {
    setToast(message);
    setToastVisible(true);
    window.setTimeout(() => setToastVisible(false), 3200);
  }, []);

  const log = useCallback((message, type = "info") => {
    setEmailLog((current) => [
      ...current,
      { message, type, time: new Date().toLocaleTimeString() },
    ]);
  }, []);

  const getAdminAuth = useCallback(() => {
    try {
      return sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
    } catch (_) {
      return "";
    }
  }, []);

  const handleSignOut = useCallback(() => {
    try {
      sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    } catch (_) {}
    setAuthed(false);
    setPwInput("");
  }, []);

  const loadData = useCallback(async () => {
    if (!window.storage) return;
    setLoadingData(true);

    try {
      const resultsResponse = await window.storage.get(STORAGE_RESULTS_KEY, true);
      setSavedResults(safeParse(resultsResponse?.value, null));
    } catch (_) {
      setSavedResults(null);
    }

    try {
      const leaderboardResponse = await window.storage.get(STORAGE_LB_KEY, true);
      const parsed = safeParse(leaderboardResponse?.value, []);
      setLeaderboard(Array.isArray(parsed) ? parsed : []);
    } catch (_) {
      setLeaderboard([]);
    }

    setLoadingData(false);
  }, []);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const handleLogin = async () => {
    if (!pwInput || loggingIn) return;
    setLoggingIn(true);
    setPwError("");

    try {
      const ok = await window.storage.adminLogin(pwInput);
      if (!ok) {
        setPwError("Incorrect password. Try again.");
        setPwInput("");
      } else {
        try {
          sessionStorage.setItem(ADMIN_TOKEN_KEY, pwInput);
        } catch (_) {}
        setAuthed(true);
      }
    } catch (_) {
      setPwError("Could not reach the server. Try again.");
    }

    setLoggingIn(false);
  };

  const publishedFinalResults = useMemo(
    () => resultsFingerprint(savedResults) === resultsFingerprint(FINAL_RESULTS),
    [savedResults]
  );

  const publishFinalResults = async () => {
    if (publishing) return;
    const auth = getAdminAuth();

    if (!auth) {
      showToast("Session expired — please sign in again");
      handleSignOut();
      return;
    }

    setPublishing(true);

    try {
      const finalPayload = cloneFinalResults();
      const saved = await window.storage.set(
        STORAGE_RESULTS_KEY,
        JSON.stringify(finalPayload),
        true,
        { auth }
      );

      if (!saved) {
        throw new Error("The server rejected the results save.");
      }

      const recalculateResponse = await fetch("/api/recalculate-leaderboard", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Password": auth,
        },
      });

      if (!recalculateResponse.ok) {
        const detail = await recalculateResponse.text().catch(() => "");
        throw new Error(detail || "Leaderboard recalculation failed.");
      }

      setSavedResults(finalPayload);
      await loadData();
      showToast("✅ Final results published and leaderboard recalculated!");
    } catch (error) {
      console.error("Final results publish failed:", error);
      showToast(`Publish failed: ${error?.message || "unknown error"}`);
    }

    setPublishing(false);
  };

  const sortedLeaderboard = useMemo(
    () => [...leaderboard].sort((a, b) => Number(b?.score || 0) - Number(a?.score || 0)),
    [leaderboard]
  );

  const usersWithEmail = sortedLeaderboard.filter((entry) => entry?.email).length;
  const perfectCount = sortedLeaderboard.filter(
    (entry) => Number(entry?.score || 0) === PERFECT_SCORE
  ).length;
  const topScore = sortedLeaderboard.length
    ? Number(sortedLeaderboard[0]?.score || 0)
    : 0;

  const sendResultsEmails = async () => {
    if (!publishedFinalResults) {
      showToast("Publish the baked-in final results before sending emails");
      return;
    }

    const recipients = sortedLeaderboard.filter((entry) => entry?.email);
    if (!recipients.length) {
      showToast("No email addresses found in the leaderboard");
      return;
    }

    const auth = getAdminAuth();
    if (!auth) {
      showToast("Session expired — please sign in again");
      handleSignOut();
      return;
    }

    setSending(true);
    setEmailLog([]);
    log(`Sending ${recipients.length} final results emails…`, "info");

    const entries = recipients.map((entry) => {
      const rank = sortedLeaderboard.findIndex(
        (candidate) => candidate?.username === entry?.username
      ) + 1;
      const totalUsers = sortedLeaderboard.length;
      const topPercent = totalUsers > 1
        ? Math.max(1, Math.round((rank / totalUsers) * 100))
        : 1;

      return {
        to_email: entry.email,
        to_name: entry.username,
        score: Number(entry.score || 0),
        max_score: PERFECT_SCORE,
        rank,
        total_users: totalUsers,
        top_percent: `the top ${topPercent}%`,
        winner_pick: entry.picks?.winner || "—",
        runner_pick: entry.picks?.runnerUp || "—",
        actual_winner: FINAL_RESULTS.winner,
        actual_runner: FINAL_RESULTS.runnerUp,
      };
    });

    try {
      const response = await fetch("/api/send-results", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Password": auth,
        },
        body: JSON.stringify({ entries }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "The email endpoint rejected the request.");
      }

      (data.results || []).forEach((result) => {
        if (result.ok) log(`✓ Sent to ${result.to}`, "ok");
        else log(`✗ Failed: ${result.to} — ${result.error || "unknown error"}`, "err");
      });

      log(`Done: ${data.sent || 0} sent, ${data.failed || 0} failed`, "info");
      showToast(`📬 ${data.sent || 0} result emails sent`);
    } catch (error) {
      console.error("Results email send failed:", error);
      log(`✗ Send failed: ${error?.message || "unknown error"}`, "err");
      showToast("Email send failed");
    }

    setSending(false);
  };

  if (!authed) {
    return (
      <>
        <style>{css}</style>
        <div className="dm-admin">
          <div className="dm-login-wrap">
            <div className="dm-login-card">
              <div className="dm-brand">✦ Drag Madness</div>
              <h1 className="dm-title">Admin Panel</h1>
              <p className="dm-sub">
                Sign in to publish the completed All Stars 11 results, recalculate
                the leaderboard, and send final-score emails.
              </p>
              <label className="dm-label" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                className="dm-input"
                type="password"
                value={pwInput}
                placeholder="••••••••••••"
                onChange={(event) => setPwInput(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleLogin()}
                autoFocus
              />
              {pwError && <div className="dm-error">⚠ {pwError}</div>}
              <button
                className="dm-btn dm-btn-gold dm-btn-block"
                onClick={handleLogin}
                disabled={!pwInput || loggingIn}
              >
                {loggingIn ? "Verifying…" : "Sign In →"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="dm-admin">
        <nav className="dm-nav">
          <div className="dm-nav-left">
            <div className="dm-brand">✦ Drag Madness</div>
            <span className="dm-badge">Final Admin</span>
          </div>

          <div className="dm-nav-tabs">
            {[
              ["results", "📋 Final Results"],
              ["leaderboard", "🏆 Leaderboard"],
              ["emails", "📬 Emails"],
            ].map(([tab, label]) => (
              <button
                key={tab}
                className={`dm-tab${activeTab === tab ? " active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>

          <button className="dm-btn dm-btn-ghost" onClick={handleSignOut}>
            Sign Out
          </button>
        </nav>

        {activeTab === "results" && (
          <main className="dm-page">
            <header className="dm-page-head">
              <h1 className="dm-page-title">Final Results Are Baked In</h1>
              <p className="dm-page-sub">
                Nothing needs to be selected, ordered, or entered. Review the confirmed
                outcome below, then publish it to shared storage and recalculate every score.
              </p>
            </header>

            <div className="dm-banner">
              <div>✅</div>
              <div className="dm-banner-copy">
                <strong>No manual result entry required.</strong> This file always publishes
                the exact final result object shown below. Existing partial results cannot
                accidentally overwrite it from this screen.
              </div>
            </div>

            <section className="dm-grid">
              {Object.entries(FINAL_RESULTS.semiFinalists).map(([key, queens]) => {
                const bracket = BRACKETS[key];
                const isPinkTie = key === "pink";
                const isPurpleAlternateRunner = key === "purple";

                return (
                  <div className="dm-card" key={key}>
                    <div className="dm-card-head">
                      <span className="dm-dot" style={{ background: bracket.color }} />
                      <span className="dm-card-title">{bracket.label}</span>
                      <span className="dm-status">Confirmed</span>
                    </div>
                    <div className="dm-card-body">
                      {queens.map((queen, index) => (
                        <div className="dm-result-row" key={queen}>
                          <span className="dm-result-rank">
                            {isPinkTie ? "=" : index === 0 ? "1" : "2"}
                          </span>
                          <span>{QUEEN_EMOJI[queen] || "✦"}</span>
                          <span className="dm-result-name">{queen}</span>
                        </div>
                      ))}

                      {key === "orange" && (
                        <div className="dm-result-note">
                          Dawn is the bracket winner; Ru selected A'Keria for the second
                          advancement slot after the tie.
                        </div>
                      )}
                      {isPinkTie && (
                        <div className="dm-result-note">
                          Crystal and Silky are interchangeable for the original winner and
                          runner-up prediction slots.
                        </div>
                      )}
                      {isPurpleAlternateRunner && (
                        <div className="dm-result-note">
                          Kennedy is the bracket winner. Either Sam or Jasmine may satisfy
                          the one original runner-up slot; the third advancement does not
                          create extra bracket points.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>

            <section className="dm-card dm-final-card">
              <div className="dm-card-head">
                <span className="dm-dot" style={{ background: "var(--gold)" }} />
                <span className="dm-card-title">Overall Final</span>
                <span className="dm-status">Season Complete</span>
              </div>
              <div className="dm-card-body">
                <div className="dm-final-grid">
                  <div className="dm-final-box">
                    <div className="dm-kicker">All Stars Winner</div>
                    <div className="dm-final-name">💎 {FINAL_RESULTS.winner}</div>
                  </div>
                  <div className="dm-final-box">
                    <div className="dm-kicker">Runner-Up</div>
                    <div className="dm-final-name">🦘 {FINAL_RESULTS.runnerUp}</div>
                  </div>
                </div>

                <div className="dm-policy">
                  <div className="dm-policy-title">How ties and twists are scored</div>
                  <div className="dm-policy-copy">{FINAL_RESULTS.scoringExplanation}</div>
                  <span className="dm-score-pill">
                    Perfect = {PERFECT_SCORE}/{PERFECT_SCORE} achievable points
                  </span>
                </div>
              </div>
            </section>

            <section className="dm-publish">
              <div>
                <div className="dm-publish-title">
                  {publishedFinalResults
                    ? "Final results are already live"
                    : "Publish final results and score everyone"}
                </div>
                <div className="dm-publish-sub">
                  {publishedFinalResults
                    ? "You may republish safely to force a fresh leaderboard recalculation."
                    : "This writes the baked-in results to drag-official-results-v1, then calls /api/recalculate-leaderboard."}
                </div>
              </div>
              <div className="dm-actions">
                <button className="dm-btn dm-btn-ghost" onClick={loadData} disabled={loadingData}>
                  {loadingData ? "Refreshing…" : "Refresh Status"}
                </button>
                <button className="dm-btn dm-btn-gold" onClick={publishFinalResults} disabled={publishing}>
                  {publishing
                    ? "Publishing…"
                    : publishedFinalResults
                      ? "Republish & Recalculate"
                      : "Publish Final Results →"}
                </button>
              </div>
            </section>
          </main>
        )}

        {activeTab === "leaderboard" && (
          <main className="dm-page">
            <header className="dm-page-head">
              <h1 className="dm-page-title">Final Leaderboard</h1>
              <p className="dm-page-sub">
                Scores shown here come from the authoritative server recalculation.
                A score of {PERFECT_SCORE} counts as a Perfect Bracket this season.
              </p>
            </header>

            <section className="dm-stats">
              <div className="dm-stat"><span className="dm-stat-num">{sortedLeaderboard.length}</span><div className="dm-stat-label">Players</div></div>
              <div className="dm-stat"><span className="dm-stat-num">{usersWithEmail}</span><div className="dm-stat-label">Email Signups</div></div>
              <div className="dm-stat"><span className="dm-stat-num">{topScore || "—"}</span><div className="dm-stat-label">Top Score</div></div>
              <div className="dm-stat"><span className="dm-stat-num">{perfectCount || "—"}</span><div className="dm-stat-label">Perfect 73s</div></div>
            </section>

            <div className="dm-table-wrap">
              {sortedLeaderboard.length === 0 ? (
                <div className="dm-empty">No submitted brackets were found.</div>
              ) : (
                <table className="dm-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Score</th>
                      <th>Winner Pick</th>
                      <th>Runner-Up Pick</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeaderboard.map((entry, index) => (
                      <tr key={`${entry?.username || "player"}-${index}`}>
                        <td className="dm-rank">#{index + 1}</td>
                        <td>{entry?.username || "Unnamed"}</td>
                        <td className="dm-score">
                          {Number(entry?.score || 0)}/{PERFECT_SCORE}
                          {Number(entry?.score || 0) === PERFECT_SCORE && (
                            <span className="dm-perfect">PERFECT</span>
                          )}
                        </td>
                        <td>{entry?.picks?.winner || "—"}</td>
                        <td>{entry?.picks?.runnerUp || "—"}</td>
                        <td>{entry?.email ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </main>
        )}

        {activeTab === "emails" && (
          <main className="dm-page">
            <header className="dm-page-head">
              <h1 className="dm-page-title">Send Final Results</h1>
              <p className="dm-page-sub">
                Sends each subscribed player their score out of the twist-adjusted
                perfect maximum of {PERFECT_SCORE}.
              </p>
            </header>

            <section className="dm-card dm-email-card">
              <div className="dm-publish-title">Final-score email batch</div>
              <div className="dm-publish-sub">
                {usersWithEmail} leaderboard {usersWithEmail === 1 ? "entry has" : "entries have"}
                an email address. Results must be published before sending.
              </div>

              <div className="dm-actions" style={{ marginTop: "1rem" }}>
                <button className="dm-btn dm-btn-ghost" onClick={loadData} disabled={loadingData}>
                  Refresh Leaderboard
                </button>
                <button
                  className="dm-btn dm-btn-pink"
                  onClick={sendResultsEmails}
                  disabled={sending || !publishedFinalResults || usersWithEmail === 0}
                >
                  {sending ? "Sending…" : `Send ${usersWithEmail} Final Email${usersWithEmail === 1 ? "" : "s"}`}
                </button>
              </div>

              {!publishedFinalResults && (
                <div className="dm-error" style={{ marginTop: "1rem" }}>
                  Publish the final results on the first tab before sending emails.
                </div>
              )}

              {emailLog.length > 0 && (
                <div className="dm-log">
                  {emailLog.map((entry, index) => (
                    <div className={`dm-log-${entry.type}`} key={`${entry.time}-${index}`}>
                      [{entry.time}] {entry.message}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </main>
        )}
      </div>

      <div className={`dm-toast${toastVisible ? " show" : ""}`}>{toast}</div>
    </>
  );
}
